import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, MapPin, CheckCircle2, ArrowRight, Home, Droplets, Layers, Square, Paintbrush, Building2 } from 'lucide-react';
import { QuoteForm } from '../components/QuoteForm';
import { FAQAccordion } from '../components/FAQAccordion';
const CITY_DATA: Record<string, {
  name: string;
  county: string;
  intro: string;
}> = {
  'harrison-township': {
    name: 'Harrison Township',
    county: 'Macomb County',
    intro: 'As our home base, Harrison Township holds a special place for the Moceri team. We know these neighborhoods inside and out — from the lakefront properties along Lake St. Clair to the established subdivisions off Crocker Boulevard. Our crews are frequently working right here in Harrison Township, and many of our team members live in the community.'
  },
  'clinton-township': {
    name: 'Clinton Township',
    county: 'Macomb County',
    intro: 'Clinton Township is one of our busiest service areas. With its mix of established neighborhoods from the 1970s and newer developments, we handle everything from aging roof replacements on Hall Road corridor homes to new construction siding in the subdivisions off Romeo Plank. Clinton Township homeowners trust Moceri for reliable, fairly-priced exterior work.'
  },
  'port-huron': {
    name: 'Port Huron',
    county: 'St. Clair County',
    intro: "Port Huron's location on Lake Huron means homes here face some of the harshest weather in Southeast Michigan. High winds off the lake, heavy snow, and lake-effect moisture demand roofing and siding that can take a beating. Moceri Residential Services has been protecting Port Huron homes since 2012, and we understand the unique challenges waterfront and near-waterfront properties face."
  },
  'sterling-heights': {
    name: 'Sterling Heights',
    county: 'Macomb County',
    intro: "Sterling Heights is Michigan's fourth-largest city, and its diverse housing stock keeps our crews busy year-round. From the ranch homes near Dodge Park to the colonials along Utica Road, we've replaced hundreds of roofs and installed miles of seamless gutters throughout Sterling Heights. Our reputation here is built on consistent quality and fair pricing."
  },
  troy: {
    name: 'Troy',
    county: 'Oakland County',
    intro: "Troy homeowners expect premium quality, and that's exactly what Moceri delivers. The well-maintained neighborhoods throughout Troy feature homes that deserve top-tier materials and expert installation. Whether you're updating the siding on a Big Beaver Road colonial or replacing windows in a Long Lake subdivision, our team brings the same meticulous attention to detail."
  },
  marysville: {
    name: 'Marysville',
    county: 'St. Clair County',
    intro: "Marysville's charming neighborhoods along the St. Clair River deserve contractors who care about preserving their character. Moceri Residential Services has been serving Marysville homeowners with quality roofing, siding, and gutter work that complements the community's well-kept residential streets. We're proud to be a trusted name in this tight-knit St. Clair County city."
  },
  'macomb-township': {
    name: 'Macomb Township',
    county: 'Macomb County',
    intro: "Macomb Township has seen tremendous growth over the past two decades, and many of those newer homes are now reaching the age where roofs, gutters, and siding need attention. Moceri Residential Services works extensively throughout Macomb Township's subdivisions, providing the same quality craftsmanship to newer homes that we bring to established neighborhoods."
  }
};
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const cityData = CITY_DATA[slug || ''];
  const cityName = cityData?.name || (slug ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Your City');
  const county = cityData?.county || 'Southeast Michigan';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const defaultIntro = `When homeowners in <strong>${cityName}, Michigan</strong> need reliable, high-quality exterior contracting, they turn to Moceri Residential Services. With over 15 years of experience serving ${county} and surrounding areas, we understand the unique challenges that Michigan weather presents to your home's exterior.`;
  const cityFAQs = [{
    question: `Do you provide free estimates in ${cityName}?`,
    answer: `Yes, we provide free, no-obligation exterior inspections and estimates for all homeowners in ${cityName} and the surrounding areas.`
  }, {
    question: `Are you licensed and insured to work in ${cityName}?`,
    answer: `Absolutely. Moceri Residential Services is a fully licensed builder in the state of Michigan and carries comprehensive liability and workers' compensation insurance.`
  }, {
    question: `What types of roofing do you install in ${cityName}?`,
    answer: `We specialize in premium architectural asphalt shingles from top brands like GAF and Owens Corning, which are ideal for the weather conditions in ${cityName} and ${county}.`
  }, {
    question: `How quickly can you respond to storm damage in ${cityName}?`,
    answer: `We prioritize emergency storm damage calls. If your home in ${cityName} has suffered roof or siding damage from high winds or hail, contact us immediately for prompt tarping and assessment.`
  }, {
    question: `What is the average cost of a roof replacement in ${cityName}?`,
    answer: `Roof replacement costs in ${cityName} vary based on the size of your roof, the shingle type selected, and the complexity of the job. We provide free, detailed written estimates so you know exactly what to expect — no surprises.`
  }];
  const serviceLinks = [{
    title: 'Roofing & Repairs',
    path: '/services/roofing',
    icon: Home
  }, {
    title: 'Gutter Services',
    path: '/services/gutters',
    icon: Droplets
  }, {
    title: 'Siding',
    path: '/services/siding',
    icon: Layers
  }, {
    title: 'Windows',
    path: '/services/windows',
    icon: Square
  }, {
    title: 'Painting',
    path: '/services/painting',
    icon: Paintbrush
  }, {
    title: 'Commercial',
    path: '/commercial',
    icon: Building2
  }];
  return <div className="w-full bg-white" data-id="element-538">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-539">
        <div className="absolute inset-0 z-0" data-id="element-540">
          <img src="https://images.unsplash.com/photo-1449844908441-8829872d2607?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-541" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-542"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 z-[2]" data-id="element-543"></div>
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-544">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-545">
            <nav className="flex items-center text-sm text-gray-300 font-body mb-6" data-id="element-546">
              <Link to="/" className="hover:text-brand-blue transition-colors" data-id="element-547">
                Home
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-548" />
              <Link to="/areas" className="hover:text-brand-blue transition-colors" data-id="element-549">
                Service Areas
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-550" />
              <span className="text-white font-semibold" data-id="element-551">{cityName}</span>
            </nav>
            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4" data-id="element-552">
              {cityName}, MI{' '}
              <span className="block text-brand-blue text-3xl md:text-4xl mt-2" data-id="element-553">
                Trusted Exterior Contractor
              </span>
            </h1>
            <p className="font-body text-lg text-gray-300" data-id="element-554">
              {county} • Licensed & Insured • Since 2009
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content & Form */}
      <section className="py-16 bg-gray-50 border-b border-gray-200" data-id="element-555">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-556">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20" data-id="element-557">
            <div className="w-full lg:w-7/12" data-id="element-558">
              <div className="flex items-center gap-3 mb-6 text-brand-blue" data-id="element-559">
                <MapPin size={28} data-id="element-560" />
                <h2 className="font-heading font-bold text-3xl text-brand-gray-dark" data-id="element-561">
                  Serving {cityName} Homeowners
                </h2>
              </div>
              <div className="w-16 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-562"></div>

              <div className="font-body text-brand-gray text-lg mb-10 leading-relaxed space-y-6" data-id="element-563">
                {cityData ? <>
                    <p data-id="element-564">{cityData.intro}</p>
                    <p data-id="element-565">
                      From complete roof replacements that stand up to heavy
                      snow loads, to seamless gutters that direct spring rains
                      safely away from your foundation, our licensed and insured
                      team delivers craftsmanship you can trust. We pride
                      ourselves on transparent pricing, punctuality, and leaving
                      your property cleaner than we found it.
                    </p>
                  </> : <>
                    <p dangerouslySetInnerHTML={{
                  __html: defaultIntro
                }} data-id="element-566"></p>
                    <p data-id="element-567">
                      From complete roof replacements that stand up to heavy
                      snow loads, to seamless gutters that direct spring rains
                      safely away from your foundation, our licensed and insured
                      team delivers craftsmanship you can trust. We pride
                      ourselves on transparent pricing, punctuality, and leaving
                      your property cleaner than we found it.
                    </p>
                  </>}
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 mb-10" data-id="element-568">
                <h3 className="font-heading font-bold text-2xl text-brand-gray-dark mb-6" data-id="element-569">
                  Our Services in {cityName}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-570">
                  {serviceLinks.map((service, index) => <Link key={index} to={service.path} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group" data-id="element-571">
                      <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center group-hover:bg-brand-blue transition-colors" data-id="element-572">
                        <service.icon size={20} className="text-brand-blue group-hover:text-white transition-colors" data-id="element-573" />
                      </div>
                      <span className="font-body font-semibold text-brand-gray-dark group-hover:text-brand-blue transition-colors" data-id="element-574">
                        {service.title}
                      </span>
                    </Link>)}
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="w-full h-48 bg-gray-200 rounded-xl overflow-hidden relative flex items-center justify-center border border-gray-300" data-id="element-575">
                <div className="relative z-10 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-lg shadow-sm font-body font-semibold text-brand-gray-dark flex items-center gap-2" data-id="element-576">
                  <MapPin size={18} className="text-brand-blue" data-id="element-577" />
                  {cityName}, MI — Service Area
                </div>
              </div>
            </div>

            {/* Right Form (Sticky) */}
            <div className="w-full lg:w-5/12" data-id="element-578">
              <div className="sticky top-28" data-id="element-579">
                <QuoteForm data-id="element-580" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-id="element-581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-582">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-583">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-584">
              {cityName} Exterior Service FAQs
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto mb-6 rounded-full" data-id="element-585"></div>
          </div>
          <FAQAccordion items={cityFAQs} data-id="element-586" />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-brand-blue text-center" data-id="element-587">
        <div className="max-w-4xl mx-auto px-4" data-id="element-588">
          <h2 className="font-heading font-bold text-3xl text-white mb-6" data-id="element-589">
            Ready to Start Your {cityName} Project?
          </h2>
          <p className="font-body text-xl text-blue-100 mb-8" data-id="element-590">
            Contact Moceri Residential Services today for your free estimate.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-591">
            <a href="#quote" className="bg-white text-brand-blue font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-gray-50 transition-colors shadow-lg" data-id="element-592">
              Get a Free Quote
            </a>
            <a href="tel:5862033701" className="bg-transparent border-2 border-white text-white font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-id="element-593">
              <MapPin size={20} data-id="element-594" /> (586) 203-3701
            </a>
          </div>
        </div>
      </section>
    </div>;
}